/**
 * Shapes returned by the stored grade endpoints.
 *
 * Both `grade` and `grade/refresh` return the same envelope, so the card, the history dropdown
 * and the full breakdown all render from one interface.
 *
 * Records calculated before the detail columns were added carry only `overall`; every other
 * block is then undefined. Use `hasDetail` from the API rather than null checking each block.
 */

export interface GradeValue {
  grade: string;
  score: number;
}

export interface ComponentWeighting {
  totalSales: number;
  salesPerSqft: number;
  realEstateQuality: number;
  bannerStrength: number;
  competitiveImpact: number;
}

/** Shared by Total Sales Performance and Sales Per Square Foot; only one annual field is set. */
export interface SalesGrade {
  grade: string;
  indexScore: number;
  annualSalesTotal?: number;
  annualSalesPerSqft?: number;
  vsTradeAreaAverage: number;
  vsBannerMarketAverage: number;
  vsMarketAverage: number;
}

export interface RealEstateQualityGrade {
  grade: string;
  score: number;
  accessibility: string;
  parking: string;
  visibility: string;
  trafficFlow: string;
  scCondition: string;
}

export interface BannerStrengthGrade {
  grade: string;
  score: number;
  creditRating: string;
  networkStability: string;
  marketPresence: string;
}

export interface GradeCompetitor {
  storeName: string;
  storeAddress: string;
  distance: number;
}

export interface CompetitiveImpactGrade {
  grade: string;
  score: number;
  driveTimeRadiusMinutes: number;
  competitors: GradeCompetitor[];
}

export interface StoreGradeRecord {
  /** Null for an unsaved preview, since it was never stored and cannot be linked to. */
  recordId: number | null;
  calculatedAt: string;
  trigger: string;
  source: string | null;
  overall: GradeValue;

  /** False for records saved before the detail columns existed. */
  hasDetail: boolean;

  componentWeighting?: ComponentWeighting;
  totalSalesPerformance?: SalesGrade;
  salesPerSqft?: SalesGrade;
  realEstateQuality?: RealEstateQualityGrade;
  bannerStrength?: BannerStrengthGrade;
  competitiveImpact?: CompetitiveImpactGrade;
  driveTimeMinutes?: number;
  tradeAreaCoveragePercentage?: number;
  marketAreaCoveragePercentage?: number;
}

export interface StoreGradeResponse {
  storeUuid: string;
  hasGrade: boolean;
  reason: string | null;
  current: StoreGradeRecord | null;
  history: StoreGradeRecord[];
}

/**
 * Why a store has no grade. The API sends the code, the analyst sees the sentence.
 */
const NO_GRADE_MESSAGES: Record<string, string> = {
  NEVER_CALCULATED: 'Not graded yet',
  INSUFFICIENT_COVERAGE: 'Not enough sales data in this area to grade this store',
  // Attributed to the service on purpose. The panel can be showing Status: Open at the same time,
  // and a flat "only open stores are graded" then reads as the card contradicting itself rather
  // than reporting what the grade service came back with.
  STORE_NOT_OPEN: 'Not graded — the grade service does not list this store as open',
  NO_CASING_DATA: 'Store survey data is missing, so this store cannot be graded'
};

/**
 * The sentence for a reason code.
 *
 * An unmapped code is shown rather than swallowed: the analyst gets the generic sentence plus the
 * code itself, so what the service actually said survives into a bug report instead of every
 * distinct cause collapsing into one message.
 */
export function noGradeMessage(reason: string | null | undefined): string {
  const code = (reason || '').trim().toUpperCase();
  if (!code) {
    return 'No grade available for this store';
  }
  return NO_GRADE_MESSAGES[code] || `No grade available for this store (${code})`;
}

/**
 * What triggered a calculation. Shown under the date in the history dropdown.
 *
 * STORE_STATUS_CHANGE is this store's own status changing; NEARBY_STORE_STATUS_CHANGE is a
 * neighbouring store changing. Both come back from the API and read very differently to an
 * analyst, so they are labelled separately.
 */
const TRIGGER_LABELS: Record<string, string> = {
  QUARTERLY_REFRESH: 'Quarterly refresh',
  SCHEDULED_REFRESH: 'Scheduled refresh',
  NEW_SALES_ESTIMATES: 'New sales estimates',
  STORE_STATUS_CHANGE: 'Store status change',
  NEARBY_STORE_STATUS_CHANGE: 'Nearby store status change',
  MANUAL_REFRESH: 'Generated manually'
};

export function triggerLabel(trigger: string | null | undefined): string {
  const key = (trigger || '').trim().toUpperCase();
  if (TRIGGER_LABELS[key]) {
    return TRIGGER_LABELS[key];
  }
  // Never show a raw enum. Turn anything unexpected into readable words.
  return key ? key.toLowerCase().replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : 'Recalculated';
}

/**
 * Turns a failed grade call into something an analyst can act on.
 *
 * The backend deliberately does not pass upstream detail through, so the status is all we have
 * to go on. Anything unrecognised falls back to a retryable message rather than showing a
 * blank card.
 */
export function gradeErrorMessage(status: number | null | undefined): string {
  switch (status) {
    case 401:
    case 403:
      return 'You do not have access to Asset Grades for this store';
    case 404:
      return 'This store could not be found';
    case 503:
      return 'The grade service is temporarily unavailable';
    case 0:
    case undefined:
    case null:
      return 'Could not reach the server. Check your connection and try again';
    default:
      return 'Could not load grade';
  }
}

/**
 * Whether retrying is worth offering. A permission problem will not fix itself, so the card
 * shows the message without a retry in that case.
 */
export function isRetryable(status: number | null | undefined): boolean {
  return status !== 401 && status !== 403 && status !== 404;
}
